import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import {
  ThicknessAnalysisContract,
  ThicknessPlotBundle,
  ThicknessPlotFigureBundle
} from "../../types";
import { useEscapeStack } from "../../hooks/useEscapeStack";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useSwipeToDismiss } from "../../hooks/useSwipeToDismiss";
import { responsivePlotTier, useElementSize, type ResponsivePlotTier } from "../../hooks/useElementSize";
import ModalCloseButton from "../ModalCloseButton";

const Plot = lazy(() => import("react-plotly.js"));

type PlotBundlePanelProps = {
  analysis: ThicknessAnalysisContract;
};

type PanelKey = "heatmap" | "surface_3d" | "slices_profile_relative" | "slices_profile";

type PanelMeta = {
  key: PanelKey;
  title: string;
};

type DisplayMode = "compact" | "expanded";

type PlotlyGraphDiv = {
  layout?: {
    xaxis?: Record<string, unknown>;
    yaxis?: Record<string, unknown>;
  };
  _fullLayout?: {
    xaxis?: { range?: [number, number] };
    yaxis?: { range?: [number, number] };
  };
};

type PlotlyLib = {
  relayout: (gd: PlotlyGraphDiv, update: Record<string, unknown>) => Promise<unknown>;
  redraw: (gd: PlotlyGraphDiv) => Promise<unknown>;
};

const PANELS: PanelMeta[] = [
  { key: "heatmap", title: "2D Heatmap" },
  { key: "surface_3d", title: "3D" },
  { key: "slices_profile_relative", title: "Slices Profile (Relative)" },
  { key: "slices_profile", title: "Slices Profile" }
];

const THICKNESS_SENSOR_LIMIT_MESSAGE =
  "No thickness data shown. The measured thickness is above 5 mm and outside the sensor limit.";
const THICKNESS_SENSOR_LIMIT_NOTE =
  "White gaps indicate areas above the 5 mm sensor limit and are outside the measurable range.";
const THREE_D_TRACE_TYPES = new Set(["surface", "scatter3d", "mesh3d"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function numericMargin(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? Math.max(value, fallback) : fallback;
}

function mergeMinimumMargins(
  inputMargin: unknown,
  minimums: { l: number; r: number; t: number; b: number }
) {
  const margin = isRecord(inputMargin) ? inputMargin : {};
  return {
    ...margin,
    l: numericMargin(margin.l, minimums.l),
    r: numericMargin(margin.r, minimums.r),
    t: numericMargin(margin.t, minimums.t),
    b: numericMargin(margin.b, minimums.b)
  };
}

function axisHasTitle(axis: Record<string, unknown>): boolean {
  const title = axis.title;
  if (typeof title === "string") return title.trim().length > 0;
  if (isRecord(title) && typeof title.text === "string") return title.text.trim().length > 0;
  return false;
}

function wrapPlotTitleText(text: string, maxLineLength: number): string {
  const normalized = text.replace(/<br\s*\/?>/gi, " ").replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLineLength) return normalized;

  const words = normalized.split(" ");
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLineLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines.join("<br>");
}

function normalizePlotTitle(title: unknown, mode: DisplayMode): unknown {
  const maxLineLength = mode === "compact" ? 24 : 38;
  if (typeof title === "string") {
    return { text: wrapPlotTitleText(title, maxLineLength) };
  }
  if (!isRecord(title)) return title;
  const text = typeof title.text === "string" ? title.text : "";
  if (!text.trim()) return title;
  return {
    ...title,
    text: wrapPlotTitleText(text, maxLineLength)
  };
}

function hasFiniteNestedValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((entry) => hasFiniteNestedValue(entry));
  }
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string" && value.trim()) return Number.isFinite(Number(value));
  return false;
}

function traceHasRenderableData(trace: unknown): boolean {
  if (!isRecord(trace)) return false;
  if ("z" in trace) return hasFiniteNestedValue(trace.z);
  if ("x" in trace || "y" in trace) {
    return hasFiniteNestedValue(trace.x) || hasFiniteNestedValue(trace.y);
  }
  return false;
}

function figureHasRenderableData(figure?: ThicknessPlotFigureBundle): boolean {
  return !!figure?.data?.some((trace) => traceHasRenderableData(trace));
}

function detect3dFigure(figure?: ThicknessPlotFigureBundle): boolean {
  if (!figure?.data?.length) return false;
  return figure.data.some((trace) => {
    if (!isRecord(trace)) return false;
    const traceType = String(trace.type ?? "");
    return THREE_D_TRACE_TYPES.has(traceType);
  });
}

function disable3dAxisSpikes(axis: unknown): Record<string, unknown> {
  const axisConfig = isRecord(axis) ? { ...axis } : {};
  return {
    ...axisConfig,
    showspikes: false,
    spikesides: false,
    showgrid: false,
    zeroline: false,
    showline: false
  };
}

function disable3dSceneSpikes(scene: unknown): Record<string, unknown> {
  const sceneConfig = isRecord(scene) ? { ...scene } : {};
  return {
    ...sceneConfig,
    xaxis: disable3dAxisSpikes(sceneConfig.xaxis),
    yaxis: disable3dAxisSpikes(sceneConfig.yaxis),
    zaxis: disable3dAxisSpikes(sceneConfig.zaxis)
  };
}

function disableAll3dSceneSpikes(layout: Record<string, unknown>, inputLayout: Record<string, unknown>) {
  const sceneKeys = new Set(
    Object.keys(inputLayout).filter((key) => /^scene\d*$/.test(key))
  );
  sceneKeys.add("scene");

  sceneKeys.forEach((key) => {
    layout[key] = disable3dSceneSpikes(layout[key] ?? inputLayout[key]);
  });
}

function schedule3dHoverCleanup(gd: PlotlyGraphDiv | null) {
  if (!gd) return;
  window.requestAnimationFrame(() => {
    import("plotly.js-dist-min")
      .then((plotly) => (plotly as unknown as PlotlyLib).redraw(gd))
      .catch(() => undefined);
  });
}

function panelLegendHint(panel: PanelKey, isMultiple: boolean, hasCi: boolean): string {
  if (panel === "heatmap" || panel === "surface_3d") {
    return "Legend: Global max/min, slice extrema, feeder wave, thickness scale";
  }
  if (isMultiple) {
    return hasCi
      ? "Legend: dashed=slices, solid black=mean, shaded=95% CI"
      : "Legend: dashed=slices, solid black=mean";
  }
  if (panel === "slices_profile_relative") {
    return "Legend: points=slices, solid black=mean, solid red=profile";
  }
  return "Legend: points=slices, solid black=mean";
}

function normalizeTraceColorbar(colorbar: unknown, tier: ResponsivePlotTier, compact: boolean) {
  const source = isRecord(colorbar) ? colorbar : {};
  if (tier === "phone") {
    return {
      ...source,
      orientation: "h",
      x: 0.5,
      xanchor: "center",
      y: -0.2,
      yanchor: "top",
      len: 0.72,
      thickness: 12,
      tickfont: {
        ...(isRecord(source.tickfont) ? source.tickfont : {}),
        size: 10
      },
      title: isRecord(source.title)
        ? {
            ...source.title,
            side: "bottom"
          }
        : source.title
    };
  }

  return {
    ...source,
    ...(compact
      ? {
          len: typeof source.len === "number" ? Math.min(Number(source.len), 0.6) : 0.6,
          thickness:
            typeof source.thickness === "number"
              ? Math.max(8, Number(source.thickness) * 0.78)
              : 10
        }
      : {})
  };
}

function scaleTrace(
  trace: unknown,
  panelKey: PanelKey,
  mode: DisplayMode,
  tier: ResponsivePlotTier
): unknown {
  if (!isRecord(trace)) return trace;

  const scaled: Record<string, unknown> = { ...trace };
  const traceType = String(scaled.type ?? "");
  const scale = mode === "compact" ? 0.78 : 1;
  const traceMode = typeof scaled.mode === "string" ? scaled.mode : "";
  const isSlicePanel = panelKey === "slices_profile" || panelKey === "slices_profile_relative";
  const isSlicePointTrace = isSlicePanel && traceMode.includes("markers");
  const traceName = String(scaled.name ?? "").toLowerCase();
  const isMeanTrace =
    isSlicePanel &&
    (traceName.includes("mean of slices") ||
      traceName.includes("smooth mean thickness by height") ||
      traceName.includes("mean thickness by height"));

  if (isRecord(scaled.line)) {
    const line = { ...scaled.line };
    if (typeof line.width === "number" && mode === "compact") {
      line.width = Math.max(1, Number(line.width) * scale);
    }
    if (isMeanTrace) {
      line.color = "#000000";
      line.dash = "solid";
      if (typeof line.width !== "number") {
        line.width = 3;
      }
    }
    scaled.line = line;
  } else if (isMeanTrace) {
    scaled.line = {
      color: "#000000",
      dash: "solid",
      width: 3
    };
  }

  if (isRecord(scaled.marker)) {
    const marker = { ...scaled.marker };
    if (isSlicePointTrace) {
      marker.opacity = 0.5;
    }
    if (typeof marker.size === "number" && mode === "compact") {
      marker.size = Math.max(2, marker.size * scale);
    }
    if (isRecord(marker.line) && typeof marker.line.width === "number" && mode === "compact") {
      marker.line = {
        ...marker.line,
        width: Math.max(0.5, Number(marker.line.width) * scale)
      };
    }
    scaled.marker = marker;
  }

  if (THREE_D_TRACE_TYPES.has(traceType)) {
    delete scaled.showspikes;
    delete scaled.spikesides;
    delete scaled.spikecolor;
    delete scaled.spikethickness;
    delete scaled.spikedash;
  }

  if (traceType === "surface") {
    const contours = isRecord(scaled.contours) ? scaled.contours : {};
    scaled.contours = {
      ...contours,
      x: { ...(isRecord(contours.x) ? contours.x : {}), show: false, highlight: false },
      y: { ...(isRecord(contours.y) ? contours.y : {}), show: false, highlight: false },
      z: { ...(isRecord(contours.z) ? contours.z : {}), show: false, highlight: false }
    };
  }

  if ((traceType === "surface" || panelKey === "heatmap") && isRecord(scaled.colorbar)) {
    scaled.colorbar = normalizeTraceColorbar(scaled.colorbar, tier, mode === "compact");
  }

  return scaled;
}

function buildLayout(
  figure: ThicknessPlotFigureBundle,
  panelKey: PanelKey,
  mode: DisplayMode,
  tier: ResponsivePlotTier
): Record<string, unknown> {
  const inputLayout = isRecord(figure.layout) ? figure.layout : {};
  const compact = mode === "compact";
  const is3d = panelKey === "surface_3d" || detect3dFigure(figure);
  const colorbarBelow = tier === "phone" && (panelKey === "heatmap" || is3d);
  const minimumMargin =
    panelKey === "heatmap"
      ? compact
        ? colorbarBelow
          ? { l: 64, r: 24, t: 94, b: 224 }
          : { l: 78, r: 112, t: 94, b: 154 }
        : colorbarBelow
          ? { l: 72, r: 28, t: 106, b: 232 }
          : { l: 92, r: 124, t: 106, b: 166 }
      : is3d
        ? compact
          ? colorbarBelow
            ? { l: 24, r: 24, t: 82, b: 170 }
            : { l: 32, r: 48, t: 82, b: 96 }
          : colorbarBelow
            ? { l: 30, r: 30, t: 96, b: 190 }
            : { l: 50, r: 60, t: 96, b: 118 }
      : compact
        ? { l: tier === "phone" ? 46 : 52, r: 24, t: 82, b: 118 }
        : { l: 80, r: 50, t: 96, b: 124 };

  const layout: Record<string, unknown> = {
    ...inputLayout,
    autosize: true,
    title: normalizePlotTitle(inputLayout.title, mode),
    margin: mergeMinimumMargins(inputLayout.margin, minimumMargin)
  };

  if (panelKey === "heatmap") {
    layout.paper_bgcolor = "#ffffff";
    layout.plot_bgcolor = "#ffffff";
  }

  if (is3d) {
    disableAll3dSceneSpikes(layout, inputLayout);
    layout.hovermode = "closest";
    layout.spikedistance = -1;
  }

  if (!is3d) {
    const inputXaxis = isRecord(inputLayout.xaxis) ? inputLayout.xaxis : {};
    const inputYaxis = isRecord(inputLayout.yaxis) ? inputLayout.yaxis : {};
    layout.xaxis = {
      ...inputXaxis,
      automargin: true,
      ...(panelKey === "heatmap" && !axisHasTitle(inputXaxis)
        ? { title: { text: "Bottle circumference / angle", standoff: compact ? 18 : 22 } }
        : {})
    };
    layout.yaxis = {
      ...inputYaxis,
      automargin: true,
      ...(panelKey === "heatmap" && !axisHasTitle(inputYaxis)
        ? { title: { text: "Bottle height", standoff: compact ? 18 : 22 } }
        : {})
    };
  }

  const legendFont =
    isRecord(inputLayout.legend) && isRecord((inputLayout.legend as Record<string, unknown>).font)
      ? ((inputLayout.legend as Record<string, unknown>).font as Record<string, unknown>)
      : {};

  layout.legend = {
    ...(isRecord(inputLayout.legend) ? inputLayout.legend : {}),
    orientation: "h",
    yanchor: "top",
    y: colorbarBelow ? -0.42 : panelKey === "heatmap" ? (compact ? -0.32 : -0.28) : compact ? -0.18 : -0.2,
    xanchor: "left",
    x: 0,
    bgcolor: "rgba(0,0,0,0)",
    font: {
      ...legendFont,
      size: panelKey === "heatmap" && compact ? 9 : compact ? 10 : 12
    }
  };

  return layout;
}

function buildDisplayFigure(
  figure: ThicknessPlotFigureBundle,
  panelKey: PanelKey,
  mode: DisplayMode,
  tier: ResponsivePlotTier
): ThicknessPlotFigureBundle {
  return {
    data: figure.data.map((trace) => scaleTrace(trace, panelKey, mode, tier)),
    layout: buildLayout(figure, panelKey, mode, tier),
    config: figure.config
  };
}

function plotConfig(panelKey: PanelKey, base?: Record<string, unknown>, compact = false) {
  const oneToOneIcon = {
    width: 500,
    height: 500,
    path: "M55,85H445V415H55Z M120,150V350H380V150Z"
  };

  const oneToOneButton = {
    name: "1:1",
    title: "Set 1:1 scale",
    icon: oneToOneIcon,
    click: async (gd: PlotlyGraphDiv) => {
      try {
        const plotly = (await import("plotly.js-dist-min")) as unknown as PlotlyLib;
        const xRange = gd?._fullLayout?.xaxis?.range;
        const yRange = gd?._fullLayout?.yaxis?.range;

        const resetUpdate: Record<string, unknown> = {
          "xaxis.constrain": "domain",
          "yaxis.constrain": "domain"
        };
        if (Array.isArray(xRange) && xRange.length === 2) {
          resetUpdate["xaxis.range"] = [xRange[0], xRange[1]];
        }
        if (Array.isArray(yRange) && yRange.length === 2) {
          resetUpdate["yaxis.range"] = [yRange[0], yRange[1]];
        }

        await plotly.relayout(gd, {
          ...resetUpdate,
          "xaxis.scaleanchor": "y",
          "xaxis.scaleratio": 1
        });
      } catch {
        // Ignore modebar custom-button failures to keep plot interaction stable.
      }
    }
  };

  return {
    responsive: true,
    displaylogo: false,
    modeBarButtonsToRemove: ["lasso2d", "select2d"],
    modeBarButtonsToAdd: panelKey === "slices_profile_relative" ? [oneToOneButton] : [],
    toImageButtonOptions: {
      format: "png",
      filename: "thickness-plot",
      scale: compact ? 1 : 2
    },
    ...(base || {})
  };
}

type PlotCardProps = {
  panel: PanelMeta;
  figure?: ThicknessPlotFigureBundle;
  isMobile: boolean;
  legendHint: string;
  onMaximize: (panel: PanelMeta) => void;
};

function PlotCard({ panel, figure, isMobile, legendHint, onMaximize }: PlotCardProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const graphRef = useRef<PlotlyGraphDiv | null>(null);
  const [visible, setVisible] = useState(false);
  const panelSize = useElementSize(panelRef);
  const measuredWidth = panelSize.width || (isMobile ? 390 : 900);
  const plotTier = responsivePlotTier(measuredWidth);
  const plotHeight =
    plotTier === "phone"
      ? panel.key === "heatmap" || panel.key === "surface_3d"
        ? 560
        : 460
      : plotTier === "narrow"
        ? panel.key === "heatmap" || panel.key === "surface_3d"
          ? 540
          : 480
        : 540;

  useEffect(() => {
    const node = panelRef.current;
    if (!node || visible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "180px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  const displayFigure = useMemo(() => {
    if (!figure) return undefined;
    return buildDisplayFigure(figure, panel.key, "compact", plotTier);
  }, [figure, panel.key, plotTier]);

  const config = useMemo(() => plotConfig(panel.key, figure?.config, true), [figure, panel.key]);
  const hasRenderableData = panel.key !== "heatmap" || figureHasRenderableData(figure);
  const emptyMessage =
    figure && panel.key === "heatmap" && !hasRenderableData
      ? THICKNESS_SENSOR_LIMIT_MESSAGE
      : "Plot unavailable";

  return (
    <div
      ref={panelRef}
      className="card"
      style={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        borderColor: "#1f334d",
        background: "var(--surface-elevated)",
        display: "flex",
        flexDirection: "column",
        gap: 8
      }}
      data-tour-id={
        panel.key === "heatmap"
          ? "thickness-detail-2d"
          : panel.key === "surface_3d"
          ? "thickness-detail-3d"
          : undefined
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap"
        }}
      >
        <div className="label" style={{ marginBottom: 0, minWidth: 0, overflowWrap: "anywhere" }}>
          {panel.title}
        </div>
        <button className="button secondary" onClick={() => onMaximize(panel)} disabled={!figure}>
          Maximize
        </button>
      </div>
      <div className="label" style={{ fontSize: 11, color: "var(--text-faint)", lineHeight: 1.35 }}>
        {legendHint}
      </div>
      <div
        className="thickness-plot-frame"
        style={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          border: "1px solid #1e2b3d",
          borderRadius: 10,
          background: "#0c1422",
          overflow: "hidden",
          cursor: "default",
          minHeight: plotHeight,
          height: plotHeight
        }}
      >
        {visible && displayFigure && hasRenderableData ? (
          <Suspense
            fallback={
              <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--text-faint)" }}>
                Loading interactive plot…
              </div>
            }
          >
            <Plot
              data={displayFigure.data as any}
              layout={displayFigure.layout as any}
              config={config as any}
              style={{ width: "100%", height: "100%" }}
              useResizeHandler
              onInitialized={(_figure: unknown, graphDiv: unknown) => {
                graphRef.current = graphDiv as PlotlyGraphDiv;
              }}
              onUpdate={(_figure: unknown, graphDiv: unknown) => {
                graphRef.current = graphDiv as PlotlyGraphDiv;
              }}
              onUnhover={() => {
                if (panel.key === "surface_3d") schedule3dHoverCleanup(graphRef.current);
              }}
            />
          </Suspense>
        ) : (
          <div
            style={{
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "var(--text-faint)",
              padding: 18,
              textAlign: "center",
              lineHeight: 1.4
            }}
          >
            {figure && hasRenderableData ? "Scroll to load plot…" : emptyMessage}
          </div>
        )}
      </div>
      {panel.key === "heatmap" ? (
        <div style={{ color: "var(--text-subtle)", fontSize: 12, lineHeight: 1.4 }}>
          {THICKNESS_SENSOR_LIMIT_NOTE}
        </div>
      ) : null}
    </div>
  );
}

type PlotModalProps = {
  panel: PanelMeta;
  figure: ThicknessPlotFigureBundle;
  legendHint: string;
  onClose: () => void;
};

function PlotModal({ panel, figure, legendHint, onClose }: PlotModalProps) {
  const graphRef = useRef<PlotlyGraphDiv | null>(null);
  const plotFrameRef = useRef<HTMLDivElement | null>(null);
  const plotFrameSize = useElementSize(plotFrameRef);
  const expandedTier = responsivePlotTier(
    plotFrameSize.width || (typeof window === "undefined" ? 1200 : window.innerWidth)
  );
  const expandedFigure = useMemo(
    () => buildDisplayFigure(figure, panel.key, "expanded", expandedTier),
    [expandedTier, figure, panel.key]
  );
  const config = useMemo(() => plotConfig(panel.key, figure.config, false), [figure.config, panel.key]);
  const hasRenderableData = panel.key !== "heatmap" || figureHasRenderableData(figure);
  useBodyScrollLock(true);
  const swipeToClose = useSwipeToDismiss<HTMLDivElement>({
    enabled: true,
    onDismiss: onClose
  });

  return (
    <div className="modal-backdrop" style={{ zIndex: 2800 }} onClick={onClose}>
      <div
        ref={swipeToClose.modalRef}
        className="modal"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(1640px, 98vw)",
          height: "96vh",
          maxWidth: "98vw",
          maxHeight: "96vh",
          padding: 12,
          border: "1px solid #35527a",
          background: "#0b1423",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 10
        }}
      >
        <div className="modal-swipe-handle" aria-hidden="true" {...swipeToClose.handleProps} />
        <div
          className="modal-drag-region"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}
          {...swipeToClose.handleProps}
        >
          <div>
            <div className="section-title" style={{ margin: 0 }}>
              {panel.title}
            </div>
            <div className="label" style={{ fontSize: 12, color: "var(--text-faint)", marginTop: 4 }}>
              {legendHint}
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>
        <div
          ref={plotFrameRef}
          className="thickness-plot-frame thickness-plot-frame-expanded"
          style={{ flex: 1, minHeight: 0, borderRadius: 10, overflow: "hidden", border: "1px solid #2c4465" }}
        >
          {hasRenderableData ? (
            <Suspense
              fallback={
                <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--text-faint)" }}>
                  Loading interactive plot…
                </div>
              }
            >
              <Plot
                data={expandedFigure.data as any}
                layout={expandedFigure.layout as any}
                config={config as any}
                style={{ width: "100%", height: "100%" }}
                useResizeHandler
                onInitialized={(_figure: unknown, graphDiv: unknown) => {
                  graphRef.current = graphDiv as PlotlyGraphDiv;
                }}
                onUpdate={(_figure: unknown, graphDiv: unknown) => {
                  graphRef.current = graphDiv as PlotlyGraphDiv;
                }}
                onUnhover={() => {
                  if (panel.key === "surface_3d") schedule3dHoverCleanup(graphRef.current);
                }}
              />
            </Suspense>
          ) : (
            <div
              style={{
                height: "100%",
                display: "grid",
                placeItems: "center",
                color: "var(--text-faint)",
                padding: 24,
                textAlign: "center",
                lineHeight: 1.4
              }}
            >
              {THICKNESS_SENSOR_LIMIT_MESSAGE}
            </div>
          )}
        </div>
        {panel.key === "heatmap" ? (
          <div style={{ color: "var(--text-subtle)", fontSize: 12, lineHeight: 1.4 }}>
            {THICKNESS_SENSOR_LIMIT_NOTE}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function PlotBundlePanel({ analysis }: PlotBundlePanelProps) {
  const bundle = analysis.plot_bundle as ThicknessPlotBundle | undefined;
  const [expandedPanel, setExpandedPanel] = useState<PanelMeta | null>(null);
  const [viewportWidth, setViewportWidth] = useState(
    typeof window === "undefined" ? 1400 : window.innerWidth
  );

  useEscapeStack(!!expandedPanel, () => setExpandedPanel(null));

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!bundle) return null;

  const isMultiple = String(analysis?.analyzer_meta?.mode ?? "").toLowerCase() === "multiple";
  const hasCi = Array.isArray(analysis?.measurement_ci_profile) && analysis.measurement_ci_profile.length > 1;
  const isMobile = viewportWidth <= 767;

  const figuresByPanel: Record<PanelKey, ThicknessPlotFigureBundle | undefined> = {
    heatmap: bundle.heatmap,
    surface_3d: bundle.surface_3d,
    slices_profile_relative: bundle.slices_profile_relative,
    slices_profile: bundle.slices_profile
  };

  const expandedFigure = expandedPanel ? figuresByPanel[expandedPanel.key] : undefined;
  const expandedLegendHint = expandedPanel
    ? panelLegendHint(expandedPanel.key, isMultiple, hasCi)
    : "";

  return (
    <>
      <div
        data-tour-id="thickness-plot-overview"
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowX: "clip"
        }}
      >
        {PANELS.map((panel) => (
          <PlotCard
            key={panel.key}
            panel={panel}
            figure={figuresByPanel[panel.key]}
            isMobile={isMobile}
            legendHint={panelLegendHint(panel.key, isMultiple, hasCi)}
            onMaximize={(selectedPanel) => setExpandedPanel(selectedPanel)}
          />
        ))}
      </div>

      {expandedPanel && expandedFigure ? (
        <PlotModal
          panel={expandedPanel}
          figure={expandedFigure}
          legendHint={expandedLegendHint}
          onClose={() => setExpandedPanel(null)}
        />
      ) : null}
    </>
  );
}
